import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import JanelaDestacada, {
  BotaoDestacar,
} from '@core/layout/janela-destacada/JanelaDestacada'
import {
  applyColorByCriterion,
  clearColorByCriterion,
  itensDoGrupo,
  type ColorByBucket,
} from '@viewer/lib/classColors'
import type {
  ColorByMode,
  ColorByCriterion,
} from '@viewer/store/viewerSlice'

// Floating "Colorir por critério" panel.
//
// Six modes:
//   entity          - IFC class (IfcWall, IfcDuctSegment, ...)
//   predefinedType  - PredefinedType / ObjectType attribute
//   system          - IfcSystem / IfcGroup membership (via raw IFC)
//   material        - RelatingMaterial via IfcRelAssociatesMaterial
//   type            - IfcRelDefinesByType (IfcWallType, IfcDoorType, ...)
//   property        - Pset_X.Y or top-level attribute Name
//
// Federation: the criterion is applied to EVERY loaded model; the
// legend is unified, so the same key (e.g. "IfcWall") gets the same
// colour across all federated IFCs. State lives in the store
// (colorByActive), so the engine re-applies it on tile spawns and
// on newly federated models without the panel needing to be open.

const MODE_LABELS: Record<ColorByMode, string> = {
  entity: 'Entidade IFC',
  predefinedType: 'PredefinedType',
  system: 'Sistema / Grupo',
  material: 'Material',
  type: 'Tipo (IfcRelDefinesByType)',
  property: 'Propriedade',
}

const MODE_HELP: Record<ColorByMode, string> = {
  entity:
    'Cada classe IFC presente nos modelos carregados (IfcWall, IfcDuctSegment, …) recebe a sua cor.',
  predefinedType:
    'Pinta pelo atributo PredefinedType (e.g. IfcWall.PredefinedType=PARTITIONING). Quando NOTDEFINED ou USERDEFINED o item fica sem cor.',
  system:
    'Cada IfcSystem / IfcGroup recebe uma cor. Itens não atribuídos via IfcRelAssignsToGroup ficam sem cor. Necessita Parser IFC paralelo activo.',
  material:
    'Pinta pelo material associado via IfcRelAssociatesMaterial (IfcMaterial, IfcMaterialList, IfcMaterialLayerSet, IfcMaterialConstituentSet).',
  type: 'Pinta pelo IfcTypeObject associado via IfcRelDefinesByType (IfcWallType, IfcDoorType, …).',
  property:
    'Sintaxe: "Pset_X.Property" para um property set, ou só "Name" para um atributo top-level. Limite de 10 000 itens por modelo.',
}

function FloatingColorByPanel() {
  const open = useAppStore((s) => s.colorByPanelOpen)
  const setOpen = useAppStore((s) => s.setColorByPanelOpen)
  const { z, trazer } = useJanelaAberta('colorir-por', open)
  const colorByActive = useAppStore((s) => s.colorByActive)
  const models = useAppStore((s) => s.models)

  const [mode, setMode] = useState<ColorByMode>(
    colorByActive?.mode ?? 'entity',
  )
  const [propertyKey, setPropertyKey] = useState(
    colorByActive?.propertyKey ?? '',
  )
  const [legend, setLegend] = useState<ColorByBucket[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [position, setPosition] = useState({ x: 200, y: 220 })
  // Destacado numa janela do browser: a legenda num segundo ecrã, e o
  // clique numa barra continua a seleccionar no 3D da janela principal.
  const [destacada, setDestacada] = useState(false)
  const voltar = useCallback(() => setDestacada(false), [])
  const fechar = useCallback(() => {
    setDestacada(false)
    setOpen(false)
  }, [setOpen])
  const dragRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { ...position },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [position],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current) return
      const { pointerStart, posStart } = dragRef.current
      setPosition({
        x: Math.max(0, posStart.x + e.clientX - pointerStart.x),
        y: Math.max(0, posStart.y + e.clientY - pointerStart.y),
      })
    }
    const onUp = () => {
      dragRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [])

  // Sync local UI controls when the criterion changes externally
  // (e.g. cleared from another path). Doesn't touch legend; that's
  // only refreshed on Aplicar.
  // Adotado durante o render com guarda de referência sobre o objeto do
  // store, em vez de um efeito. A condição `if (colorByActive)` mantém-se,
  // logo limpar o critério continua a não mexer nos controlos locais.
  const [prevColorByActive, setPrevColorByActive] = useState(colorByActive)
  if (prevColorByActive !== colorByActive) {
    setPrevColorByActive(colorByActive)
    if (colorByActive) {
      setMode(colorByActive.mode)
      setPropertyKey(colorByActive.propertyKey ?? '')
    }
  }

  const apply = async () => {
    if (models.length === 0) {
      setError('Carrega um IFC primeiro.')
      return
    }
    const criterion: ColorByCriterion =
      mode === 'property'
        ? { mode, propertyKey: propertyKey.trim() }
        : { mode }
    if (mode === 'property' && !criterion.propertyKey) {
      setError('Indica a propriedade (e.g. Pset_WallCommon.IsExternal).')
      return
    }
    setError(null)
    setBusy(true)
    try {
      const { legend: newLegend } = await applyColorByCriterion(criterion)
      setLegend(newLegend)
      if (newLegend.length === 0) {
        setError(
          'Nenhum elemento corresponde ao critério. Verifica se o Parser IFC paralelo está activo (Diagnóstico) para os modos Sistema / Material / Tipo.',
        )
      }
    } catch (err) {
      console.warn('[ColorByPanel] apply failed', err)
      setError('Falha ao aplicar critério. Vê a consola para detalhes.')
    } finally {
      setBusy(false)
    }
  }

  const clear = async () => {
    setBusy(true)
    setError(null)
    try {
      await clearColorByCriterion()
      setLegend([])
    } finally {
      setBusy(false)
    }
  }

  const totalElements = useMemo(
    () => legend.reduce((acc, b) => acc + b.count, 0),
    [legend],
  )
  // A legenda é também um gráfico de barras ligado ao modelo: a barra
  // mede quantos elementos tem cada grupo, e clicar selecciona-os no 3D
  // (com Shift, junta à selecção). Ideia do IFClite (@ifc-lite/charts,
  // MPL-2.0); aqui sem biblioteca de gráficos, porque a legenda já tinha
  // os números e as cores.
  const [porQuantidade, setPorQuantidade] = useState(false)
  const legendaOrdenada = useMemo(
    () => (porQuantidade ? [...legend].sort((a, b) => b.count - a.count) : legend),
    [legend, porQuantidade],
  )
  const maximo = useMemo(() => legend.reduce((m, b) => Math.max(m, b.count), 0), [legend])
  const seleccionarGrupo = (chave: string, juntar: boolean) => {
    const alvos = itensDoGrupo(chave)
    if (alvos.length === 0) return
    useAppStore.getState().applyBulkSelection(alvos, juntar ? 'add' : 'replace')
  }

  if (!open) return null

  if (models.length === 0) {
    return (
      <div
        onPointerDownCapture={trazer}
        className="fixed w-[320px] rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-[11px] text-[var(--color-text-muted)] shadow-2xl"
        style={{ zIndex: z, left: position.x, top: position.y }}
      >
        Carrega um IFC para colorir por critério.
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="ml-2 rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px]"
        >
          ×
        </button>
      </div>
    )
  }

  const activeBadge = colorByActive ? (
    <span className="ml-2 rounded bg-[var(--color-accent-bg)] px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider text-[var(--color-accent)]">
      {MODE_LABELS[colorByActive.mode]}
      {colorByActive.propertyKey ? ` · ${colorByActive.propertyKey}` : ''}
    </span>
  ) : null

  const corpo = (
    <div className="flex flex-col gap-2 p-3 text-[11px]">
      <label className="flex items-center gap-2">
        <span className="w-16 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
          Critério
        </span>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as ColorByMode)}
          className="flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] outline-none focus:border-[var(--color-accent)]"
        >
          {(Object.keys(MODE_LABELS) as ColorByMode[]).map((m) => (
            <option key={m} value={m}>
              {MODE_LABELS[m]}
            </option>
          ))}
        </select>
      </label>
      <div className="text-[10px] italic text-[var(--color-text-muted)]">
        {MODE_HELP[mode]}
      </div>
      {mode === 'property' && (
        <input
          type="text"
          value={propertyKey}
          onChange={(e) => setPropertyKey(e.target.value)}
          placeholder="Pset_WallCommon.IsExternal  ou  Name"
          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] outline-none focus:border-[var(--color-accent)]"
        />
      )}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => void apply()}
          disabled={busy}
          className="flex-1 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1 text-[11px] text-[var(--color-accent)] hover:bg-[var(--color-accent)]/20 disabled:opacity-40"
        >
          {busy ? 'A colorir…' : 'Aplicar'}
        </button>
        <button
          type="button"
          onClick={() => void clear()}
          disabled={busy || !colorByActive}
          className="rounded border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
        >
          Limpar critério
        </button>
      </div>
      {error && (
        <div className="rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] p-1.5 text-[10px] text-[var(--color-warn-text)]">
          {error}
        </div>
      )}
      {legend.length > 0 && (
        <div className="mt-1 flex max-h-72 flex-col overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-bg)]/40">
          <div className="flex items-center border-b border-[var(--color-border)] px-2 py-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            <span>
              Legenda · {legend.length} grupo(s) · {totalElements} elemento(s)
            </span>
            <label className="ml-auto flex items-center gap-1 normal-case tracking-normal">
              <input
                type="checkbox"
                checked={porQuantidade}
                onChange={(e) => setPorQuantidade(e.target.checked)}
              />
              por quantidade
            </label>
          </div>
          <ul className="flex flex-col gap-px overflow-auto p-1">
            {legendaOrdenada.map((b) => (
              <li key={b.key}>
                <button
                  type="button"
                  onClick={(e) => seleccionarGrupo(b.key, e.shiftKey)}
                  title={`Seleccionar os ${b.count} elemento(s) de "${b.key}" (Shift junta à selecção)`}
                  className="relative flex w-full items-center gap-1.5 overflow-hidden rounded px-1 py-0.5 text-left hover:bg-[var(--color-bg)]"
                >
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 opacity-20"
                    style={{
                      width: `${maximo > 0 ? (b.count / maximo) * 100 : 0}%`,
                      background: b.color,
                    }}
                  />
                  <span
                    className="relative h-3 w-3 shrink-0 rounded border border-[var(--color-border)]"
                    style={{ background: b.color }}
                  />
                  <span className="relative truncate text-[10px] text-[var(--color-text)]">
                    {b.key}
                  </span>
                  <span className="relative ml-auto font-mono text-[9px] text-[var(--color-text-dim)]">
                    {b.count}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-[var(--color-border)] px-2 py-1 text-[9px] text-[var(--color-text-dim)]">
            Clica numa barra para seleccionar esses elementos no 3D; Shift junta à selecção.
          </div>
        </div>
      )}
    </div>
  )

  if (destacada) {
    return (
      <JanelaDestacada
        aberta
        nome="vn2r-colorir-por"
        titulo="Colorir por critério"
        features="width=420,height=760,resizable=yes,scrollbars=yes"
        oQue="o Colorir por critério"
        aoVoltar={voltar}
        aoFechar={fechar}
      >
        {activeBadge && <div className="px-3 pt-2">{activeBadge}</div>}
        {corpo}
      </JanelaDestacada>
    )
  }

  return (
    <div
      role="dialog"
      aria-label="Colorir por critério"
      onPointerDownCapture={trazer}
      className="fixed flex w-[360px] flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl"
      style={{ zIndex: z, left: position.x, top: position.y }}
    >
      <div
        onPointerDown={onTitleDown}
        className="flex h-7 shrink-0 cursor-move items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs text-[var(--color-text)] select-none"
      >
        <span className="flex items-center">
          Colorir por critério
          {activeBadge}
        </span>
        <span className="ml-auto" onPointerDown={(e) => e.stopPropagation()}>
          <BotaoDestacar
            onClick={() => setDestacada(true)}
            rotulo="Destacar o colorir por critério para uma janela do browser"
            className="rounded px-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          />
        </span>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setOpen(false)}
          className="rounded px-1.5 py-0 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          title="Fechar"
        >
          ×
        </button>
      </div>
      {corpo}
    </div>
  )
}

export default FloatingColorByPanel
